"use client";

import { cn } from "@/lib/utils";
import { type Variants, motion } from "framer-motion";
import {
	AdaptiveText,
	type AdaptiveTextProps,
	Title,
	type TitleProps,
} from "./Content";
import { Sizes } from "./Content/styles";
import { Label } from "./ui/label";

type Props = {
	eyebrow: string;
	title: TitleProps["title"];
	description: AdaptiveTextProps["portableText"];
	graphic: React.ReactNode;
	fade?: ("top" | "bottom")[];
	className?: string;
	index?: number;
};

// Main card container animation
const cardVariants: Variants = {
	hidden: {
		opacity: 0,
		y: 20,
	},
	visible: (index = 0) => ({
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.5,
			delay: 0.15 * index,
			ease: "easeOut",
			staggerChildren: 0.1,
		},
	}),
};

// Graphics animation
const graphicVariants: Variants = {
	hidden: {
		opacity: 0,
		scale: 0.95,
	},
	visible: {
		opacity: 1,
		scale: 1,
		transition: {
			duration: 0.6,
			ease: "easeOut",
		},
	},
};

// Text content animation
const contentVariants: Variants = {
	hidden: {
		opacity: 0,
		y: 10,
	},
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.4,
			ease: "easeOut",
			staggerChildren: 0.05,
		},
	},
};

// Individual text elements animation
const textItemVariants: Variants = {
	hidden: {
		opacity: 0,
		y: 5,
	},
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.3,
			ease: "easeOut",
		},
	},
};

export const BentoCard = ({
	className = "",
	eyebrow,
	title,
	description,
	graphic,
	fade = [],
	index = 0,
}: Props) => {
	return (
		<motion.div
			initial="hidden"
			animate="visible"
			variants={cardVariants}
			custom={index}
			className={cn(
				className,
				"group relative flex flex-col overflow-hidden rounded-lg",
				"bg-white shadow-xs ring-1 ring-primary",
			)}
		>
			<div className="relative h-80 shrink-0 overflow-hidden">
				<motion.div className="h-full w-full" variants={graphicVariants}>
					{graphic}
				</motion.div>
				{fade.includes("top") && (
					<div className="absolute inset-0 bg-linear-to-b from-white to-50% group-data-dark:from-[-25%] group-data-dark:from-gray-800" />
				)}
				{fade.includes("bottom") && (
					<div className="absolute inset-0 bg-linear-to-t from-white to-50% group-data-dark:from-[-25%] group-data-dark:from-gray-800" />
				)}
			</div>
			<motion.div className="relative p-10" variants={contentVariants}>
				<motion.div variants={textItemVariants}>
					<Label className="font-mono font-semibold text-gray-500 text-xs/5 uppercase tracking-widest sm:text-xs/5 md:text-xs/5">
						{eyebrow}
					</Label>
				</motion.div>
				<motion.div variants={textItemVariants}>
					<Title as="h3" size={Sizes.Card}>
						{title}
					</Title>
				</motion.div>
				<motion.div variants={textItemVariants}>
					<AdaptiveText portableText={description} size={Sizes.Card} />
				</motion.div>
			</motion.div>
		</motion.div>
	);
};
